import { Routes } from '@angular/router';
import {ListViewComponent} from './components/list-view/list-view.component';
import {DynamicFormComponent} from './components/dynamic-form/dynamic-form.component';
import {CustomersPageComponent} from './page-components/customers-page/customers-page.component';
import {StripeRedirectPageComponent} from './page-components/stripe-redirect-page/stripe-redirect-page.component';
import {SchedulerPageComponent} from './page-components/scheduler-page/scheduler-page.component';
import {LandingPageComponent} from './components/landing-page/landing-page.component';
import {LoginPageComponent} from './components/login-page/login-page.component';
import {DashboardComponent} from './components/dashboard/dashboard.component';
import {DashboardHomeComponent} from './components/dashboard-home/dashboard-home.component';
import {AboutPageComponent} from './components/about-page/about-page.component';
import {ContactPageComponent} from './components/contact-page/contact-page.component';
import {ServicesPageComponent} from './components/services-page/services-page.component';
import {PlaceholderPageComponent} from './components/placeholder-page/placeholder-page.component';
import {GeneralPestControlComponent} from './components/services/general-pest-control/general-pest-control.component';
import {AntControlComponent} from './components/services/ant-control/ant-control.component';
import {CockroachControlComponent} from './components/services/cockroach-control/cockroach-control.component';
import {SpiderControlComponent} from './components/services/spider-control/spider-control.component';
import {WaspControlComponent} from './components/services/wasp-control/wasp-control.component';
import {RodentControlComponent} from './components/services/rodent-control/rodent-control.component';
import {MosquitoControlComponent} from './components/services/mosquito-control/mosquito-control.component';
import {PestLibraryComponent} from './components/pest-library/pest-library.component';
import {PestDetailComponent} from './components/pest-detail/pest-detail.component';
import {ServiceAreasComponent} from './components/service-areas/service-areas.component';
import {FaqComponent} from './components/faq/faq.component';
import {OurTeamComponent} from './components/our-team/our-team.component';
import {CareersComponent} from './components/careers/careers.component';
import {PreventionTipsComponent} from './components/prevention-tips/prevention-tips.component';
import {EarlyBirdLandingComponent} from './components/early-bird-landing/early-bird-landing.component';
import {PrivacyPolicyComponent} from './components/privacy-policy/privacy-policy.component';
import {TermsOfServiceComponent} from './components/terms-of-service/terms-of-service.component';
import {CommissionSplitComponent} from './components/commission-split/commission-split.component';
import {AuthGuard} from './guards/auth.guard';
import {AdminGuard} from './guards/admin.guard';
import {TechnicianGuard} from './guards/technician.guard';
import {SalesGuard} from './guards/sales.guard';

export const routes: Routes = [
  { path: '', component: LandingPageComponent },
  { path: 'early-bird', component: EarlyBirdLandingComponent },
  { path: 'about', component: AboutPageComponent },
  { path: 'contact', component: ContactPageComponent },
  { path: 'services', component: ServicesPageComponent },
  
  // Service-specific routes
  { path: 'general-pest-control', component: GeneralPestControlComponent },
  { path: 'ant-control', component: AntControlComponent },
  { path: 'cockroach-control', component: CockroachControlComponent },
  { path: 'spider-control', component: SpiderControlComponent },
  { path: 'wasp-control', component: WaspControlComponent },
  { path: 'rodent-control', component: RodentControlComponent },
  { path: 'mosquito-control', component: MosquitoControlComponent },
  { path: 'commercial-services', component: PlaceholderPageComponent, data: { title: 'Commercial Services' } },
  
  // About-specific routes
  { path: 'our-team', component: OurTeamComponent },
  { path: 'service-areas', component: ServiceAreasComponent },
  { path: 'careers', component: CareersComponent },
  
  // Resource routes
  { path: 'pest-library', component: PestLibraryComponent },
  { path: 'pest-library/:id', component: PestDetailComponent },
  { path: 'resources/pest-library', redirectTo: '/pest-library' },
  { path: 'resources/prevention', component: PreventionTipsComponent },
  { path: 'resources/faqs', component: FaqComponent },
  { path: 'faq', redirectTo: '/resources/faqs' },
  { path: 'blog', component: PlaceholderPageComponent, data: { title: 'Blog' } },
  { path: 'privacy', component: PrivacyPolicyComponent },
  { path: 'terms', component: TermsOfServiceComponent },
  
  { path: 'login', component: LoginPageComponent },
  
  // Portal Routes - Using Dashboard component for consistent layout
  // All users go to dashboard, but get different menus based on groups
  // Individual portal pages are accessible via direct routes with guards
  
  { 
    path: 'dashboard', 
    component: DashboardComponent,
    canActivate: [AuthGuard],
    children: [
      { path: '', component: DashboardHomeComponent },
      { path: 'commission-split', component: CommissionSplitComponent },
      { path: 'scheduler', component: SchedulerPageComponent },
      { path: 'configuration', loadComponent: () => import('./page-components/configuration-page/configuration-page.component').then(m => m.ConfigurationPageComponent) },
      { path: 'list/:model', component: ListViewComponent },
      { path: 'form/:model/:id', component: DynamicFormComponent },
      { path: 'customers/:id', component: CustomersPageComponent },
      { path: 'stripe_session/:sessionId/:customerId/:state', component: StripeRedirectPageComponent },
      
      // Admin Portal Pages
      { path: 'admin', loadComponent: () => import('./portals/admin/admin-dashboard/admin-dashboard.component').then(m => m.AdminDashboardComponent), canActivate: [AdminGuard] },
      { path: 'admin/users', loadComponent: () => import('./portals/admin/user-management/user-management.component').then(m => m.UserManagementComponent), canActivate: [AdminGuard] },
      { path: 'admin/settings', loadComponent: () => import('./portals/admin/system-settings/system-settings.component').then(m => m.SystemSettingsComponent), canActivate: [AdminGuard] },
      { path: 'admin/reports', loadComponent: () => import('./portals/admin/admin-reports/admin-reports.component').then(m => m.AdminReportsComponent), canActivate: [AdminGuard] },
      { path: 'admin/service-sync', loadComponent: () => import('./portals/admin/service-sync/service-sync.component').then(m => m.ServiceSyncComponent), canActivate: [AdminGuard] },
      { path: 'admin/schedules', loadComponent: () => import('./portals/admin/admin-schedules/admin-schedules.component').then(m => m.AdminSchedulesComponent), canActivate: [AdminGuard] },
      
      // Sales Portal Pages
      { path: 'sales/leads', loadComponent: () => import('./portals/sales/sales-leads/sales-leads.component').then(m => m.SalesLeadsComponent), canActivate: [SalesGuard] },
      { path: 'sales/performance', loadComponent: () => import('./portals/sales/sales-performance/sales-performance.component').then(m => m.SalesPerformanceComponent), canActivate: [SalesGuard] },
      { path: 'sales/my-dashboard', loadComponent: () => import('./components/my-sales-dashboard/my-sales-dashboard.component').then(m => m.MySalesDashboardComponent), canActivate: [SalesGuard] },
      { path: 'sales/user/:userId', loadComponent: () => import('./components/sales-rep-dashboard/sales-rep-dashboard.component').then(m => m.SalesRepDashboardComponent), canActivate: [SalesGuard] },
      { path: 'sales/user/:userId/customers', loadComponent: () => import('./components/sales-rep-customer-list/sales-rep-customer-list.component').then(m => m.SalesRepCustomerListComponent), canActivate: [SalesGuard] },
      
      // Technician Portal Pages
      { path: 'employee/schedule', loadComponent: () => import('./portals/employee/employee-schedule/employee-schedule.component').then(m => m.EmployeeScheduleComponent), canActivate: [TechnicianGuard] },
      { path: 'employee/customers', loadComponent: () => import('./portals/employee/employee-customers/employee-customers.component').then(m => m.EmployeeCustomersComponent), canActivate: [TechnicianGuard] },
      { path: 'employee/services', loadComponent: () => import('./portals/employee/employee-services/employee-services.component').then(m => m.EmployeeServicesComponent), canActivate: [TechnicianGuard] },
      
      // Customer Portal Pages
      { path: 'customer', loadComponent: () => import('./portals/customer/customer-dashboard/customer-dashboard.component').then(m => m.CustomerDashboardComponent) },
      { path: 'customer/services', loadComponent: () => import('./portals/customer/customer-services/customer-services.component').then(m => m.CustomerServicesComponent) },
      { path: 'customer/billing', loadComponent: () => import('./portals/customer/customer-billing/customer-billing.component').then(m => m.CustomerBillingComponent) },
      { path: 'customer/support', loadComponent: () => import('./portals/customer/customer-support/customer-support.component').then(m => m.CustomerSupportComponent) }
    ]
  },
  
  // Legacy routes for backward compatibility
  {path: 'scheduler', redirectTo: '/dashboard/scheduler'},
  {path: 'list/:model', redirectTo: '/dashboard/list/:model'},
  {path: 'form/:model/:id', redirectTo: '/dashboard/list/:model'},
  {path: 'form/:model/:id', redirectTo: '/dashboard/form/:model/:id'},
  {path: 'customers/:id', redirectTo: '/dashboard/customers/:id'},
  {path: 'stripe_session/:sessionId/:customerId/:state', redirectTo: '/dashboard/stripe_session/:sessionId/:customerId/:state'}
];
